import { ChangeDetectionStrategy, Component, computed, input, model } from '@angular/core';
import { Search } from 'lucide';
import { NbIcon } from '../../icon/icon';
import { injectNbUiText } from '../../i18n/ui-text';

/**
 * `<nb-search-field>` — the design-system search pill
 * (Mockup `.searchbar .input` + `.input-wrap` + leading magnifier).
 *
 * A 46px pill-shaped text input with a leading `<nb-icon>` magnifier. There is
 * no visible label, so an accessible name is required via `ariaLabel` (rendered
 * on the native `<input>` as `aria-label`). The text value is a two-way model.
 *
 * @example
 * <nb-search-field [(value)]="q" ariaLabel="Søk i nabolaget" />
 * <nb-search-field [(value)]="q" placeholder="Søk etter naboer" ariaLabel="Søk" />
 */
@Component({
  selector: 'nb-search-field',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NbIcon],
  template: `<div class="input-wrap searchbar">
    <nb-icon [icon]="Search" />
    <input
      class="input"
      type="search"
      role="searchbox"
      [value]="value()"
      [placeholder]="placeholderText()"
      [attr.aria-label]="labelText()"
      (input)="value.set($any($event.target).value)"
    />
  </div>`,
  styleUrl: './search-field.css',
})
export class NbSearchField {
  /** Two-way bound search text. */
  readonly value = model('');
  /** Placeholder shown when empty. Defaults to the mockup's Norwegian 'Søk'. */
  readonly placeholder = input<string | undefined>(undefined);
  /** Accessible name for the field (there is no visible label). */
  readonly ariaLabel = input<string | undefined>(undefined);
  private readonly uiText = injectNbUiText();
  /** Rendered placeholder / accessible name — the inputs when given, else the app's design-system copy. */
  protected readonly placeholderText = computed(() => this.placeholder() ?? this.uiText().search);
  protected readonly labelText = computed(() => this.ariaLabel() ?? this.uiText().search);

  protected readonly Search = Search;
}
